/**
 * Create a vehicle, route or driver without leaving trip entry.
 *
 * Same idea as Alt+C in voucher entry, and for the same reason: a clerk
 * halfway through a page of paper slips should not have to abandon it,
 * navigate to Fleet, add a truck and find their place again. Losing
 * fifteen half-typed rows to add one plate number is how people go back
 * to the notebook.
 *
 * Each dialog is deliberately the SMALLEST form that produces a valid
 * master. A vehicle needs a plate; everything else can be filled in on
 * the Fleet screen later, when nobody is mid-batch. The full forms live
 * there.
 *
 * Every dialog returns the created record so the caller can select it in
 * the row that is waiting for it and put the cursor back.
 */
import { useEffect, useState } from 'react'
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { createDriver, createRoute, createVehicle } from '../api/endpoints'
import { errorMessage } from '../api/client'
import type { Driver, TransportRoute, Vehicle } from '../api/types'
import { c } from '../theme'
import { normalise, sanitize } from '../vouchers/money'
import SubcontractorPicker from './SubcontractorPicker'

/** Shared chrome so the three dialogs cannot drift apart. */
function QuickDialog({
  open, title, onClose, onSave, saveDisabled, busy, error, children,
}: {
  open: boolean
  title: string
  onClose: () => void
  onSave: () => void
  saveDisabled: boolean
  busy: boolean
  error: string | null
  children: React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>{children}</Stack>
        <Typography sx={{ fontSize: 11.5, color: c.inkMuted, mt: 2 }}>
          {t('quick.finishLater')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">{t('common.cancel')}</Button>
        <Button variant="contained" onClick={onSave}
          disabled={saveDisabled || busy}>
          {busy ? t('common.loading') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Enter saves, so the whole dialog is one keystroke to dismiss. */
function onEnter(fn: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); fn() }
  }
}


// --------------------------------------------------------------------------

export function QuickVehicleDialog({ open, onClose, onCreated }: {
  open: boolean
  onClose: () => void
  onCreated: (v: Vehicle) => void
}) {
  const { t } = useTranslation()
  const [plate, setPlate] = useState('')
  const [kind, setKind] = useState<'OWN' | 'SUBCONTRACTED'>('OWN')
  const [sub, setSub] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setPlate(''); setKind('OWN'); setSub(''); setError(null)
  }, [open])

  // A hired truck with no owner produces trips that generate a cost
  // nobody can be paid for. The server refuses it; so does this.
  const ready = !!plate.trim() && (kind === 'OWN' || !!sub)

  async function save() {
    if (!ready) return
    setBusy(true); setError(null)
    try {
      onCreated(await createVehicle({
        plate_no: plate.trim(), kind,
        subcontractor_code: kind === 'SUBCONTRACTED' ? sub : null }))
      onClose()
    } catch (e) { setError(errorMessage(e)) } finally { setBusy(false) }
  }

  return (
    <QuickDialog open={open} title={t('quick.newVehicle')} onClose={onClose}
      onSave={save} saveDisabled={!ready} busy={busy} error={error}>
      <TextField label={t('fleet.plate')} value={plate} autoFocus
        onChange={(e) => setPlate(e.target.value.toUpperCase())}
        onKeyDown={onEnter(save)} />
      <TextField select label={t('fleet.kind')} value={kind}
        onChange={(e) => setKind(e.target.value as typeof kind)}>
        <MenuItem value="OWN">{t('fleet.own')}</MenuItem>
        <MenuItem value="SUBCONTRACTED">{t('fleet.hired')}</MenuItem>
      </TextField>
      {kind === 'SUBCONTRACTED' && (
        <SubcontractorPicker value={sub} onChange={setSub} />
      )}
    </QuickDialog>
  )
}


// --------------------------------------------------------------------------

export function QuickRouteDialog({ open, onClose, onCreated }: {
  open: boolean
  onClose: () => void
  onCreated: (r: TransportRoute) => void
}) {
  const { t } = useTranslation()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [rate, setRate] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setFrom(''); setTo(''); setRate(''); setError(null)
  }, [open])

  const ready = !!from.trim() && !!to.trim()

  async function save() {
    if (!ready) return
    setBusy(true); setError(null)
    try {
      onCreated(await createRoute({
        from_place: from.trim(), to_place: to.trim(),
        default_rate: rate.trim() ? normalise(rate) : null }))
      onClose()
    } catch (e) { setError(errorMessage(e)) } finally { setBusy(false) }
  }

  return (
    <QuickDialog open={open} title={t('quick.newRoute')} onClose={onClose}
      onSave={save} saveDisabled={!ready} busy={busy} error={error}>
      <TextField label={t('fleet.from')} value={from} autoFocus
        onChange={(e) => setFrom(e.target.value)} onKeyDown={onEnter(save)} />
      <TextField label={t('fleet.to')} value={to}
        onChange={(e) => setTo(e.target.value)} onKeyDown={onEnter(save)} />
      <TextField label={t('fleet.houseRate')} value={rate}
        onChange={(e) => setRate(sanitize(e.target.value))}
        onKeyDown={onEnter(save)}
        helperText={t('fleet.houseRateHint')} />
    </QuickDialog>
  )
}


// --------------------------------------------------------------------------

export function QuickDriverDialog({ open, onClose, onCreated }: {
  open: boolean
  onClose: () => void
  onCreated: (d: Driver) => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setName(''); setPhone(''); setError(null)
  }, [open])

  async function save() {
    if (!name.trim()) return
    setBusy(true); setError(null)
    try {
      onCreated(await createDriver({
        name: name.trim(), phone: phone.trim() || null }))
      onClose()
    } catch (e) { setError(errorMessage(e)) } finally { setBusy(false) }
  }

  return (
    <QuickDialog open={open} title={t('quick.newDriver')} onClose={onClose}
      onSave={save} saveDisabled={!name.trim()} busy={busy} error={error}>
      <TextField label={t('fleet.driverName')} value={name} autoFocus
        onChange={(e) => setName(e.target.value)} onKeyDown={onEnter(save)} />
      <TextField label={t('fleet.phone')} value={phone}
        onChange={(e) => setPhone(e.target.value)} onKeyDown={onEnter(save)} />
    </QuickDialog>
  )
}
